import { useEffect, useRef, useCallback } from 'react';
import { logger } from '@/lib/logger';

interface TimelineRenderMetrics {
  timelineRenderStart: number;
  timelineRenderEnd: number;
  itemsRendered: number;
  virtualScrollTime: number;
  layoutShifts: number;
}

interface UseTimelineRenderObserverOptions {
  enabled?: boolean;
  itemCount: number;
  onMetricsCollected?: (metrics: TimelineRenderMetrics) => void;
}

export const useTimelineRenderObserver = ({
  enabled = true,
  itemCount,
  onMetricsCollected
}: UseTimelineRenderObserverOptions) => {
  const metricsRef = useRef<Partial<TimelineRenderMetrics>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const layoutShiftObserverRef = useRef<PerformanceObserver | null>(null);
  const timelineContainerRef = useRef<HTMLElement | null>(null);

  // Start timeline render timing
  const startRenderTiming = useCallback(() => {
    if (!enabled) return;
    
    metricsRef.current.timelineRenderStart = performance.now();
    metricsRef.current.itemsRendered = itemCount;
    metricsRef.current.layoutShifts = 0;
    
    logger.debug('[Timeline Performance] Start render timing', {
      itemCount,
      timestamp: metricsRef.current.timelineRenderStart
    });
  }, [enabled, itemCount]);

  // End timeline render timing
  const endRenderTiming = useCallback(() => {
    if (!enabled || !metricsRef.current.timelineRenderStart) return;
    
    metricsRef.current.timelineRenderEnd = performance.now();
    const renderTime = metricsRef.current.timelineRenderEnd - metricsRef.current.timelineRenderStart;
    
    const metrics: TimelineRenderMetrics = {
      timelineRenderStart: metricsRef.current.timelineRenderStart,
      timelineRenderEnd: metricsRef.current.timelineRenderEnd,
      itemsRendered: metricsRef.current.itemsRendered || 0,
      virtualScrollTime: metricsRef.current.virtualScrollTime || 0,
      layoutShifts: metricsRef.current.layoutShifts || 0,
    };
    
    logger.debug('[Timeline Performance] Render timing complete', {
      renderTime: renderTime.toFixed(2),
      itemsRendered: metrics.itemsRendered,
      layoutShifts: metrics.layoutShifts,
      avgTimePerItem: metrics.itemsRendered > 0 ? (renderTime / metrics.itemsRendered).toFixed(2) : 0
    });

    // Alert on performance issues
    if (renderTime > 300) {
      logger.warn('[Timeline Performance] Slow timeline render detected', {
        renderTime: renderTime.toFixed(2),
        itemCount: metrics.itemsRendered,
        threshold: 300
      });
    }

    if (metrics.layoutShifts > 3) {
      logger.warn('[Timeline Performance] Excessive layout shifts detected', {
        layoutShifts: metrics.layoutShifts,
        threshold: 3
      });
    }

    onMetricsCollected?.(metrics);
  }, [enabled, onMetricsCollected]);

  // Set up layout shift observer
  useEffect(() => {
    if (!enabled || !('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'layout-shift') {
            const layoutShiftEntry = entry as any; // LayoutShift is not fully typed
            if (!layoutShiftEntry.hadRecentInput) {
              metricsRef.current.layoutShifts = (metricsRef.current.layoutShifts || 0) + 1;
            }
          }
        }
      });

      observer.observe({ type: 'layout-shift', buffered: true });
      layoutShiftObserverRef.current = observer;

      return () => {
        observer.disconnect();
      };
    } catch (error) {
      logger.warn('[Timeline Performance] Failed to setup layout shift observer', error);
    }
  }, [enabled]);

  // Set up intersection observer for virtual scrolling metrics
  const setupIntersectionObserver = useCallback((container: HTMLElement) => {
    if (!enabled || !container) return;

    timelineContainerRef.current = container;

    const observer = new IntersectionObserver(
      (entries) => {
        const virtualScrollStart = performance.now();
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Item became visible
          }
        });
        metricsRef.current.virtualScrollTime = performance.now() - virtualScrollStart;
      },
      {
        root: container,
        rootMargin: '50px',
        threshold: 0
      }
    );

    // Observe all timeline items
    const items = container.querySelectorAll('[data-timeline-item]');
    items.forEach(item => observer.observe(item));

    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [enabled]);

  // Cleanup observers
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (layoutShiftObserverRef.current) {
        layoutShiftObserverRef.current.disconnect();
      }
    };
  }, []);

  // Record virtual scroll performance
  const recordVirtualScrollPerformance = useCallback((scrollTime: number) => {
    metricsRef.current.virtualScrollTime = scrollTime;
  }, []);

  return {
    startRenderTiming,
    endRenderTiming,
    setupIntersectionObserver,
    recordVirtualScrollPerformance,
    timelineContainerRef
  };
};